import React from 'react';
import { observer } from 'mobx-react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import DndTypes from './DnDTypes';
import { canDropColumnOnCell, useOETDragState } from './dndContext';
import type { ColumnDragItem } from './DnDTypes';
import type { IOETColumn } from '../../stores/model/uimodel/oetcolumn';

/** The own (caller-supplied) props the header cell reads. */
export interface OETHeaderCellProps {
  column: IOETColumn;
  selectable: boolean;
  onSelect: (id: string) => void;
  selected: string | null;
}

/**
 * A cell in the OET header.
 *
 * Each cell is both a drag source (the column it carries) and a drop target for
 * another column. The drag source is wired with `useDraggable`; the drop target
 * with `useDroppable`. The dragged column moves to *after* the cell it is
 * dropped on (`to = column.index + 1`, computed in the container's
 * `onDragEnd`); `canDrop` no-ops on self/neighbour.
 *
 * - column: the column
 * - selectable: is selection enabled for the object type in the cell?
 * - onSelect: callback to call when the object type is selected. Arguments: id.
 * - selected: id of the currently selected element
 */
const OETHeaderCell = observer(function OETHeaderCell({
  column,
  selectable,
  onSelect,
  selected,
}: OETHeaderCellProps) {
  const dragItem: ColumnDragItem = { type: DndTypes.COLUMN, index: column.index };

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `oet-col-src-${column.id}`,
    data: dragItem,
  });
  const { setNodeRef: setDropRef } = useDroppable({
    id: `oet-col-tgt-${column.id}`,
    data: { kind: 'column-cell', columnIndex: column.index },
  });

  const setNodeRef = (node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  };

  const { activeItem, overId } = useOETDragState();
  const isOver = overId === `oet-col-tgt-${column.id}`;
  const canDrop =
    activeItem != null &&
    activeItem.type === DndTypes.COLUMN &&
    canDropColumnOnCell(activeItem, column.index);

  const classes: string[] = [];
  if (column.object.abstract) {
    // Was `.oet-content .oet-table thead tr th.abstract { color: $grey2 }` in
    // the old SCSS; converted to the token utility on this (live) header cell.
    classes.push('text-grey2');
  }
  if (selectable) {
    classes.push('selectable');
  }
  if (selected === column.object.id) {
    classes.push('selected');
  }
  if (isDragging) {
    classes.push('dragging');
  }
  if (isOver && canDrop) {
    classes.push('drop-highlight');
  }
  return (
    <th
      ref={setNodeRef}
      className={classes.join(' ')}
      onClick={selectable ? () => onSelect(column.object.id) : undefined}
      {...listeners}
      {...attributes}
    >
      <div className="text-vertical">
        <div className="inner-text">{column.object.name}</div>
      </div>
    </th>
  );
});

export default OETHeaderCell;
